import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import {
  BULK_UNITS, DEFAULT_HOLE_TOLERANCE_THRESHOLDS, REQUIRED_HOLE_TOLERANCE_METRICS, SiteHoleTolerance, bulkBelowPercentFloor,
  toThresholdDtos, withMissingThresholds, withThresholdUnits,
} from 'src/app/bo/site-hole-tolerance';
import {
  SegmentedControlComponent, SegmentedOption,
} from 'src/app/features/blast/blast-holes/shared/segmented-control/segmented-control.component';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HoleService } from 'src/app/services/hole.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { ToleranceBarComponent } from 'src/app/shared/components/tolerance-bar/tolerance-bar.component';

@Component({
  selector: 'app-hole-tolerance',
  templateUrl: './hole-tolerance.component.html',
  styleUrls: ['./hole-tolerance.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, SegmentedControlComponent, ToleranceBarComponent],
})
export class HoleToleranceComponent {

  private readonly holeService = inject(HoleService);
  protected readonly utilService = inject(UtilisateurService);
  private readonly fonctionsService = inject(FonctionsService);

  protected formData: Partial<SiteHoleTolerance> = {};

  /** Unités au choix pour le seuil vrac : des symboles, sans traduction. */
  protected readonly bulkUnitOptions: SegmentedOption[] = BULK_UNITS.map(unit => ({ id: unit, label: unit }));
  /** En % : sous-charge limitée à -100 % (trou vide), signalée dès la saisie. */
  protected readonly bulkBelowPercentFloor = bulkBelowPercentFloor;
  protected readonly showError = signal(false);
  protected readonly isLoaded = signal(false);
  protected readonly editing = signal(false);
  protected readonly hasChanges = signal(false);

  private savedSnapshot = '';

  constructor() {
    effect(() => {
      const site = this.utilService.currentSite();
      if (site?.siteId) {
        this.loadTolerance(site.siteId);
      } else {
        this.formData = {};
        this.isLoaded.set(true);
      }
    });
  }

  protected isRequired(metric: string): boolean {
    return REQUIRED_HOLE_TOLERANCE_METRICS.includes(metric);
  }

  protected startEditing(): void {
    this.savedSnapshot = JSON.stringify(this.formData.thresholds ?? []);
    this.editing.set(true);
    this.hasChanges.set(false);
  }

  protected cancelEditing(): void {
    this.formData = {
      ...this.formData,
      thresholds: JSON.parse(this.savedSnapshot || '[]'),
    };
    this.editing.set(false);
    this.hasChanges.set(false);
    this.showError.set(false);
  }

  protected onValueChange(): void {
    const current = JSON.stringify(this.formData.thresholds);
    this.hasChanges.set(current !== this.savedSnapshot);
  }

  private loadTolerance(siteId: number): void {
    this.isLoaded.set(false);
    this.editing.set(false);
    this.holeService.getSiteHoleTolerance(siteId).subscribe({
      next: result => {
        this.formData = {
          ...result,
          thresholds: withMissingThresholds(withThresholdUnits(result.thresholds)),
        };
        this.isLoaded.set(true);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.formData = { siteId, thresholds: DEFAULT_HOLE_TOLERANCE_THRESHOLDS.map(t => ({ ...t })) };
        } else {
          this.fonctionsService.launch_toast(false);
        }
        this.isLoaded.set(true);
      },
    });
  }

  protected save(): void {
    const thresholds = this.formData.thresholds ?? [];

    const missingRequired = REQUIRED_HOLE_TOLERANCE_METRICS.some(metric => {
      const t = thresholds.find(x => x.metric === metric);
      return !t?.asDesignedMax;
    });

    const intervalInvalid = thresholds.some(t =>
      t.inToleranceMax < t.asDesignedMax ||
      (t.asDesignedMin != null && t.inToleranceMin != null && t.inToleranceMin > t.asDesignedMin)
    );

    if (missingRequired || intervalInvalid || thresholds.some(bulkBelowPercentFloor)) {
      this.showError.set(true);
      return;
    }

    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.fonctionsService.loading_screen(true);
    this.holeService.putSiteHoleTolerance(siteId, { siteId, thresholds: toThresholdDtos(thresholds) }).subscribe({
      next: result => {
        this.formData = { ...result, thresholds: withThresholdUnits(result.thresholds ?? []) };
        this.showError.set(false);
        this.editing.set(false);
        this.hasChanges.set(false);
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }
}
